import type React from 'react';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface DropdownProps {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
    className?: string;
}

// Ported from TailAdmin's ui/dropdown/Dropdown.tsx — an anchored popover
// (not a modal/slide-in panel): positioned by the parent's `relative` wrapper,
// closes on outside click unless the click originated on a `.dropdown-toggle`
// trigger button (so the same click that opens it doesn't immediately close it).
export const Dropdown: React.FC<DropdownProps> = ({
    isOpen,
    onClose,
    children,
    className = '',
}) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;

        const handleClickOutside = (event: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node) &&
                !(event.target as HTMLElement).closest('.dropdown-toggle')
            ) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    return (
        <div
            ref={dropdownRef}
            // `top-full` is load-bearing: without an explicit `top`, an absolutely
            // positioned element falls back to its flow "static position" — which
            // lands below a preceding sibling inside a `block` wrapper, but near
            // the wrapper's own top inside a `flex` one (the sibling isn't stacked
            // underneath, since flex lays children out side by side). Anchoring
            // explicitly keeps this consistent regardless of the trigger's layout.
            className={twMerge('absolute top-full z-40 right-0 mt-2 rounded-xl border border-gray-200 bg-white shadow-theme-lg', className)}
        >
            {children}
        </div>
    );
};
